import React, { useState, useEffect } from 'react';
import {
  X,
  Folder,
  Tag,
  Trash2,
  AlertTriangle,
  Check,
  Plus,
} from 'lucide-react';

// Move Note to Category Modal
export function MoveToCategoryModal({
  open,
  note,
  categories = [],
  onClose,
  onSave,
}) {
  const [selectedCategory, setSelectedCategory] = useState('');

  useEffect(() => {
    if (note) {
      setSelectedCategory(note.category || categories[0]?.name || 'Personal');
    }
  }, [note, categories]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (open) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  if (!open || !note) return null;

  const handleSave = (e) => {
    e.preventDefault();
    onSave(note, selectedCategory);
    onClose();
  };

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div
        className="modal-container glass-panel note-category-select-modal animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Move Note to Category"
      >
        <div className="modal-header">
          <div className="modal-header-info">
            <div className="modal-icon-badge" style={{ background: 'rgba(99, 102, 241, 0.15)', color: '#6366f1' }}>
              <Folder size={18} />
            </div>
            <div>
              <h3 className="modal-title">Move to Category</h3>
              <p className="modal-subtitle">Choose a destination category for "{note.title || 'Untitled'}".</p>
            </div>
          </div>
          <button type="button" className="btn-icon modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSave} className="modal-body">
          <div className="category-select-grid">
            {categories.map((c) => {
              const isSelected = selectedCategory === c.name;
              return (
                <button
                  key={c.id || c.name}
                  type="button"
                  className={`category-select-option ${isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedCategory(c.name)}
                >
                  <span
                    className="note-category-color-dot"
                    style={{ background: c.color || '#3b82f6' }}
                  />
                  <span className="category-select-name">{c.name}</span>
                  {isSelected && <Check size={16} className="category-select-check" />}
                </button>
              );
            })}
          </div>

          <div className="modal-footer" style={{ padding: '16px 0 0 0' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Category
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Manage Tags on a Note Modal
export function ManageNoteTagsModal({
  open,
  note,
  onClose,
  onSave,
}) {
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (note) {
      setTags(Array.isArray(note.tags) ? [...note.tags] : []);
      setTagInput('');
    }
  }, [note]);

  // Lock body scroll on mobile
  useEffect(() => {
    if (open) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  if (!open || !note) return null;

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const val = tagInput.trim().replace(/^#/, '').toLowerCase();
      if (val && !tags.includes(val)) {
        setTags([...tags, val]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (t) => {
    setTags(tags.filter((item) => item !== t));
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSave(note, tags);
    onClose();
  };

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div
        className="modal-container glass-panel note-tags-manage-modal animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Manage Note Tags"
      >
        <div className="modal-header">
          <div className="modal-header-info">
            <div className="modal-icon-badge" style={{ background: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
              <Tag size={18} />
            </div>
            <div>
              <h3 className="modal-title">Manage Tags</h3>
              <p className="modal-subtitle">Add or remove keyword tags for "{note.title || 'Untitled'}".</p>
            </div>
          </div>
          <button type="button" className="btn-icon modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSave} className="modal-body">
          <div className="form-group">
            <label className="input-label">Tags</label>
            <div className="editor-tags-list" style={{ minHeight: '48px' }}>
              {tags.map((tg) => (
                <span key={tg} className="editor-tag-chip">
                  #{tg}
                  <button type="button" onClick={() => handleRemoveTag(tg)} className="editor-tag-remove">
                    <X size={12} />
                  </button>
                </span>
              ))}
              <input
                type="text"
                className="editor-tag-input"
                placeholder="Type tag and press Enter..."
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={handleAddTag}
                autoFocus
              />
            </div>
            <span className="input-hint">Type a tag name and press Enter or comma to add.</span>
          </div>

          <div className="modal-footer" style={{ padding: '16px 0 0 0' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary">
              Save Tags
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Permanent Delete Modal
export function PermanentDeleteModal({
  open,
  note,
  onClose,
  onConfirm,
}) {
  // Lock body scroll on mobile
  useEffect(() => {
    if (open) document.body.classList.add('mobile-modal-open');
    else document.body.classList.remove('mobile-modal-open');
    return () => document.body.classList.remove('mobile-modal-open');
  }, [open]);

  if (!open || !note) return null;

  return (
    <div className="modal-overlay animate-fade-in" onClick={onClose}>
      <div
        className="modal-container glass-panel note-confirm-delete-modal animate-scale-in"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Permanently Delete Note"
      >
        <div className="modal-header">
          <div className="modal-header-info">
            <div className="modal-icon-badge" style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#ef4444' }}>
              <AlertTriangle size={20} />
            </div>
            <div>
              <h3 className="modal-title">Permanently Delete Note</h3>
              <p className="modal-subtitle">This action cannot be undone.</p>
            </div>
          </div>
          <button type="button" className="btn-icon modal-close-btn" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="modal-body">
          <p>
            Are you sure you want to permanently delete <strong>"{note.title || 'Untitled Note'}"</strong>?
            The encrypted ciphertext and all associated version history and attachments will be purged from the zero-knowledge vault.
          </p>
        </div>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => {
              onConfirm(note);
              onClose();
            }}
          >
            <Trash2 size={14} />
            <span>Permanently Delete</span>
          </button>
        </div>
      </div>
    </div>
  );
}
